import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DefaultMessage from '../Messages/DefaultMessage';
import CarouselModal from '../Modals/CarouselModal';
import styled from 'styled-components';
import ReadOnlyMessageSidePreview from '../ReadOnlyContent/ReadOnlyMessageSidePreview';
import { AuthContext } from '../../util/context';

const noOp = () => undefined;
const accessCapabilities = {
    canEdit: false,
    canManage: false,
    canGenerate: false,
    canAdd: false,
    canCreateFolders: false,
    canReadThreads: false
};

// Use the regular message card with an isolated, read-only view context. Public
// content must never inherit the signed-in account's active folder permissions.
export default function SharedMessageContent({ message }) {
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [imageOpen, setImageOpen] = React.useState(false);
    React.useEffect(() => {
        setImageOpen(false);
        setPreviewOpen(false);
    }, [message.id]);
    const folder = React.useMemo(() => ({ id: `public:${message.id}`, accessCapabilities }), [message.id]);
    const displayMessage = React.useMemo(() => {
        return {
            ...message,
            id: `public:${message.id}`,
            folder
        };
    }, [message, folder]);
    const context = React.useMemo(
        () => ({
            state: {
                selectedFolder: folder,
                selectedMessage: null,
                holdingKeys: false,
                keys: {},
                selectedMessagesMap: {},
                showMultiSelect: false,
                messagesToSend: [],
                showDeletedMessages: false,
                uploadProgress: {},
                uploadingMessages: {},
                treeData: { items: {} }
            },
            context: {
                setSelectedMessage: () => setPreviewOpen(true),
                setSelectedFolder: noOp,
                setSearchActive: noOp,
                setSearchTerm: noOp,
                setMessagesToSend: noOp,
                setSelectedMessagesMap: noOp,
                setShowMultiSelect: noOp,
                setSelectedUsername: noOp,
                setShowSocialModal: noOp,
                setHoldingKeys: noOp,
                setClientError: noOp,
                setUploadProgress: noOp
            }
        }),
        [folder]
    );
    return (
        <DndProvider backend={HTML5Backend}>
            <AuthContext.Provider value={context}>
                <SharedLayout $previewOpen={previewOpen}>
                    <MessageColumn>
                        <DefaultMessage
                            message={displayMessage}
                            index={0}
                            screenName="Shared message"
                            readOnly
                            hideQuickActions
                            hideCheckboxSection
                            hideMessageReference
                            disableThreadAttachTarget
                            showThreadAction={false}
                            showThreadHint={false}
                            suppressHover
                            showDateAtBottom
                            onMessageClick={() => setPreviewOpen(true)}
                            sharedArtifactBadges={message.artifactBadges}
                            imagePreview={() => setImageOpen(true)}
                        />
                    </MessageColumn>
                    {previewOpen && (
                        <ReadOnlyMessageSidePreview message={displayMessage} onClose={() => setPreviewOpen(false)} />
                    )}
                </SharedLayout>
                {imageOpen && (
                    <CarouselModal
                        isOpen
                        items={[message.image]}
                        currentMessage={displayMessage}
                        fileType="Image"
                        closeModal={() => setImageOpen(false)}
                        readOnly
                        allowExtraction={false}
                    />
                )}
            </AuthContext.Provider>
        </DndProvider>
    );
}

const SharedLayout = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 20px;
    width: 100%;
    max-width: ${props => (props.$previewOpen ? '1180px' : '760px')};
    margin: 0 auto;
    min-width: 0;
    > :last-child:not(:first-child) {
        height: calc(100dvh - 110px);
        position: sticky;
        top: 20px;
    }
    @media (max-width: 920px) {
        > :last-child:not(:first-child) {
            position: fixed;
            top: 72px;
            height: auto;
        }
    }
`;
const MessageColumn = styled.div`
    flex: 1;
    min-width: 0;
`;
